

import React, { useCallback, useEffect, useState } from 'react';
import { Prompt, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FaSave } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import ContentContainer from '../../../../components/ContentContainer';
import { FormBuilder, FormStateBuilder } from '../../../../components/FormBuilder';
import useAlertNotification from '../../../../context/AlertNotificationContext';
import ErrorRequestService from '../../../../services/errorRequest';
import UtilService from '../../../../services/util';
import ImpostoApiService from '../../../../services/api/ImpostoApiService';
import ImpostoFaixaFromInfo from './ImpostoFaixaFormInfo';
import FaixaImpostoValidation from '../../../../services/validation/FaixaImpostoValidation';

/**
 * Formulário para adicionar uma faixa de imposto
 */
function ImpostoFaixaForm({ dados, onSave, onCancel, editable=false }) {
    // CONTEXT
    const match = useRouteMatch();
    const location = useLocation();
    const history = useHistory();
    const alert = useAlertNotification();
    
    // PROPS
    const { cod } = match.params;
    const { sigla=null } = location.state;

    // STATE
    const [origData, setOrginData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [formFields, setFormFields] = useState(null);
    const [edit, setEdit] = useState([]);
    const [isEdited, setIsEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // FUNCTIONS
    const isFormEdited = () => {
        if (!edit.length) return false;
        const jsonOrigData = JSON.stringify(origData);
        const jsonFormData = JSON.stringify(formData);
        return (jsonOrigData !== jsonFormData);
    }
        
    const askForLeave = (cbPositive, cbNegative=()=>{}) => {
        if (cbPositive instanceof Function) {
            const msg = 'Foram feitas alterações!\nDeseja realmente sair e perdê-las?';
            const leave = window.confirm(msg);
            
            if (leave) cbPositive();
            else cbNegative();
        }   
        return false;
    }

    const resetForm = () => UtilService.genericResetFormFunction();
    
    const handleOnCancel = () => {
        if (onCancel) {
            onCancel();
            return;
        }
        history.goBack();
    }

    const hanldeOnSave = () => {
        if (onSave) {
            onSave();
            return;
        } 
        const url = `${match.url.replace(`/${cod}`, '')}`;
        history.replace(url, location.state);
    }

    const saveForm = async () => {
        try {
            // validar form
            const validation = await FaixaImpostoValidation.validate(formData);
            if (!validation.isValid) {
                alert.error('Erros no formulário!');
                return;
            }
            // se ok, enviar p/ API
            const resp = !dados // se novo, ou existente
                ? await ImpostoApiService.createFaixa(cod, formData)
                : await ImpostoApiService.updateFaixa(cod, formData);

            if (resp.data) {
                setIsEdited(false);
                // setEdit(false);
                alert.success(`Faixa de imposto ${!dados ? 'criado' : 'atualizado'}!`);
                hanldeOnSave();
            }
        }
        catch (err) {
            const errorService = new ErrorRequestService(err);
            const errorMsg = errorService.getErrors(); 
            console.log(errorMsg);
            alert.error(errorMsg);
        }
    }

    const handleCancel = () => {
        if (isEdited) {
            askForLeave(() => {
                setIsEdited(false);
                setFormData(origData);
                resetForm();
                handleOnCancel();
            });
            return;
        }
        handleOnCancel();
    }
    
    const onChange = (ev) => {
        const { value, name } = ev.target;
        const newform = {...formData, [name]: value};
        setFormData(newform);
        setEdit([...edit, name]);
    }

    const onEdit = () => {
        if (isFormEdited() && !isEdited) setIsEdited(true);
    }

    const onStart = useCallback(async () => {
        try {
            const formCampos = ImpostoFaixaFromInfo.getFormFields();
            const formDados = dados ? {...dados} : FormStateBuilder(formCampos);

            setFormFields( formCampos );
            setFormData( formDados );
            setOrginData( formDados );
        }
        catch (err) {
            const errorService = new ErrorRequestService(err);
            const errorMsg = errorService.getErrors(); 
            console.log(errorMsg);
            alert.error(errorMsg);
        }
        finally {
            setIsLoading(false);
        }
    }, []);

    // USEEFFECTS
    useEffect(onStart, []);
    useEffect(onEdit, [formData]);

    const actions = [
        { 
            label: 'Cancelar',
            icon: <MdClose /> ,
            color: 'danger',
            type: 'button',
            onClick: handleCancel
        }
    ]

    if (editable) {
        actions.unshift(
            { 
                label: 'Salvar',
                icon: <FaSave />,
                color: 'success',
                type: 'button',
                onClick: saveForm
            }
        );
    }

    const title = `Faixa de Imposto ${sigla ? `(${sigla})` : ''}`;

    if (isLoading) return <div>Carregando...</div>;

    return (
        <ContentContainer
            title={title}
            actions={actions}
        >
            { origData && 
                <FormBuilder 
                    formFields={formFields}
                    formData={formData}
                    onChange={onChange}
                    disabledForm={!editable}
                    initialValues={origData}
                    schema={FaixaImpostoValidation.schema}
                />
            }
            
            <Prompt
                when={isEdited}
                message={location => askForLeave(() => {
                    setIsEdited(false);
                    setTimeout(() => {
                        history.push(location.pathname);
                    }, 100);
                })}
            />
        </ContentContainer>
    )
}

export default ImpostoFaixaForm;
